import L from "leaflet";
import { MonitoreoPlagaEnfermedadItem } from "../../../../../generated/grpc/catalogos_monitoreo_pb";
import {
  DatosMonitoreo,
  LoteMonitoreado,
} from "../../../../../modulos/monitoreo/sesion/agruparPorSemana";
import {
  formatNivel,
  formatPorcentaje,
} from "../../../../../modulos/monitoreo/sesion/formatIncidencia";
import {
  ESTADO_LABEL,
  evaluarEnLote,
  getAbreviatura,
  getEstadoLoteEvaluado,
  getPeorPlagaEvaluada,
  MonitoreoEstado,
  SeleccionPlagas,
} from "../../../../../modulos/monitoreo/sesion/semaforo";
import { ClienteFincaLoteResponse } from "../../../../../modulos/terceros/response/ClienteFincaLoteResponse";
import {
  BaseMapRender,
  MapGroup,
  PopupConfig,
  PopupItem,
} from "../../../../base/map/BaseMapRender";
import { ConfigTopActions } from "../../../../implementations/map/TopActions";
import { ESTADO_COLOR } from "../dashboard/monitoreoAnalisisPaleta";

/** Datos de un lote en la semana mostrada. Es lo que `BaseMapRender` asocia a
 *  cada polígono. */
export type LoteSemanaResponse = {
  /** Id del lote (calza con `ClienteFincaLoteResponse.id`). */
  id: string;
  /** Borrado lógico: nunca aplica acá, pero lo pide el contrato de la base. */
  isEliminado: boolean;
  /** Todo lo medido en el lote esa semana.
   *
   *  Antes acá viajaba solo `inc`. No alcanza: la incidencia necesita
   *  `afectados` y `muestra`, y el umbral contra el que se compara sale de las
   *  referencias congeladas de la sesión, no del catálogo vigente. */
  lote: LoteMonitoreado;
};

/** Relleno del polígono por estado del semáforo.
 *
 *  Van en literal y no como `var(--sx-…)`: el mapa usa `preferCanvas: true`, y
 *  el canvas 2D de Leaflet no resuelve variables CSS en `fillColor` — solo
 *  colores ya resueltos. Mismo motivo por el que `MonitoreoBrocaMapRender`
 *  duplica los suyos. */
const RELLENO: Record<MonitoreoEstado, string> = {
  ok: "rgba(22, 163, 74, 0.55)",
  warn: "rgba(217, 119, 6, 0.55)",
  alert: "rgba(220, 38, 38, 0.55)",
};

/** Estilo del lote que no se monitoreó esa semana: transparente, para
 *  distinguirlo del que sí se recorrió y salió limpio (verde). */
const STYLE_SIN_DATOS: L.PathOptions = {
  fillColor: "transparent",
  color: "rgb(224, 73, 13)",
  weight: 2.5,
  opacity: 1,
};

/** Mapa de la vista de sesión: los lotes de la finca coloreados según el
 *  semáforo de la **plaga activa** en la **semana activa**.
 *
 *  `_seleccion === null` no significa "no pintar" sino **modo "Todas"**: el lote
 *  toma el peor estado entre todas sus plagas. No hizo falta un centinela para
 *  distinguirlo del estado vacío porque el lote no recorrido ya sale
 *  transparente antes, por `response === null`. Con un grupo seleccionado la
 *  regla es la misma acotada a esas plagas, y con una sola plaga es el caso de
 *  un elemento: un polígono tiene un color solo, salga de una plaga o de nueve.
 *
 *  Una sola instancia viva. El sidebar le cambia la semana (`setSemana`) y la
 *  barra de plagas la selección (`setSeleccion`); ninguna de las dos recrea el mapa ni
 *  vuelve a parsear polígonos: las capas GeoJSON se arman una vez en
 *  `connect()` (con los lotes que la base ya trae cacheados de IndexedDB) y
 *  después solo se les cambia el estilo.
 *
 *  Los datos de la semana salen del endpoint del dashboard, no del `…/list`
 *  pesado: para pintar el semáforo de un lote alcanza con su `inc`. */
export class MonitoreoSesionMapRender extends BaseMapRender<LoteSemanaResponse> {
  constructor() {
    super();
    // El mapa Leaflet (y `_groupList`) se arma async: si el sidebar seleccionó
    // una semana antes de que terminara, se aplica recién acá.
    this.setOnMapInitializeEnd(() => {
      this._mapReady = true;
      this._refresh();
    });
  }

  /** Catálogo de plagas, para umbrales y nombres. Lo inyecta la página cuando
   *  termina de cargarlo. */
  setPlagas(plagas: MonitoreoPlagaEnfermedadItem[]): void {
    this._plagasById = new Map(plagas.map((plaga) => [plaga.id, plaga]));
    this._refresh();
  }

  /** Muestra las incidencias elegidas en el sidebar: la semana completa, o
   *  solo un recorrido si se marcó una fecha. */
  setDatos(datos: DatosMonitoreo | null): void {
    this._datos = datos;
    this._refresh();
  }

  /** Cambia las plagas que colorean los polígonos: una, un grupo entero, o
   *  `null` para el modo "Todas". */
  setSeleccion(seleccion: SeleccionPlagas): void {
    this._seleccion = seleccion;
    this._refresh();
  }

  /** Sin datos propios: la semana llega por `setSemana`. */
  protected doInitData(): Promise<LoteSemanaResponse[]> {
    return Promise.resolve([]);
  }

  /** Datos del lote dentro de la semana activa (o null si no se recorrió). */
  protected doGetResponse(
    lote: ClienteFincaLoteResponse,
    _responseList: LoteSemanaResponse[],
  ): LoteSemanaResponse | null {
    const porLote = this._datos?.[lote.id];
    if (!porLote) return null;
    return { id: lote.id, isEliminado: false, lote: porLote };
  }

  protected doDispose(): void {}

  /** Colorea el lote con el semáforo de la plaga activa —o con el peor estado
   *  de todas, en modo "Todas". Sin semana o sin recorrido cae al estilo
   *  transparente. */
  protected getStyle(
    _lote: ClienteFincaLoteResponse,
    response: LoteSemanaResponse | null,
  ): L.PathOptions {
    const estado = this._getEstadoLote(response);
    // Copia nueva: el click handler de la base muta el objeto devuelto
    // (`style.color = "yellow"` al seleccionar), y devolver la constante
    // compartida por referencia la dejaría corrompida para todos los lotes.
    if (!estado) return { ...STYLE_SIN_DATOS };

    return {
      fillColor: RELLENO[estado],
      fillOpacity: 1,
      color: "rgb(224, 73, 13)",
      weight: 2.5,
      opacity: 1,
    };
  }

  /** El panel superior lo arma la página (pills de plaga + acceso al reporte)
   *  vía `onMountEnd`; acá no se declara ningún selector propio. Ortomosaico
   *  sí —ayuda a ubicarse en el terreno—, conteo no (es de ganadería). */
  protected getConfigTopActions(): ConfigTopActions<LoteSemanaResponse> {
    return {
      toolTipItems: [],
      withOrtomosaico: true,
      withConteo: false,
      onMountEnd: (element) => this._onTopActionsMount?.(element),
    };
  }

  /** Registra quién monta el panel superior (la página inyecta ahí las pills
   *  de plaga y el botón del reporte). */
  setOnTopActionsMount(handler: (element: HTMLElement) => void): void {
    this._onTopActionsMount = handler;
  }

  /** Popup del lote: nombre y área (como el resto de los mapas) más, si se
   *  recorrió, la incidencia de la plaga activa con su estado. */
  protected getPopupConfig(): PopupConfig<LoteSemanaResponse> | null {
    return {
      bindItems: (lote, response) => this._popupItems(lote, response),
      withEditLote: false,
    };
  }

  /** Si el mapa ya terminó de inicializar (`_groupList` listo). */
  private _mapReady = false;
  /** Incidencias mostradas (semana completa o un recorrido). */
  private _datos: DatosMonitoreo | null = null;
  /** Plagas que colorean (`null` = modo "Todas"). */
  private _seleccion: SeleccionPlagas = null;
  /** Catálogo de plagas indexado por id. */
  private _plagasById = new Map<string, MonitoreoPlagaEnfermedadItem>();
  /** Handler del montaje del panel superior. */
  private _onTopActionsMount: ((element: HTMLElement) => void) | null = null;

  /** Estado con el que se pinta el lote. La regla vive en `getEstadoLote`
   *  (`semaforo.ts`) y no acá: el contador de la leyenda del sidebar cuenta
   *  exactamente estos colores, y con dos implementaciones podrían discrepar. */
  private _getEstadoLote(
    response: LoteSemanaResponse | null,
  ): MonitoreoEstado | null {
    return getEstadoLoteEvaluado(
      response?.lote,
      this._seleccion,
      this._plagasById,
    );
  }

  /** La plaga activa cuando se eligió UNA sola.
   *
   *  Es la que puede mostrar su valor crudo sin ambigüedad. Con un grupo o con
   *  "Todas" no hay una plaga que represente al lote: la etiqueta y el popup
   *  tienen que resolver cuál manda (`getPeorPlagaLote`) y nombrarla, o el
   *  color no se puede accionar. */
  private _getPlagaUnica(): MonitoreoPlagaEnfermedadItem | undefined {
    if (this._seleccion?.length !== 1) return undefined;
    return this._plagasById.get(this._seleccion[0]);
  }

  /** Re-pinta estilo y popup de cada lote según semana y plaga actuales.
   *  No-op hasta que el mapa termine de inicializar. */
  private _refresh(): void {
    if (!this._mapReady) return;
    this._groupList.forEach((group) => {
      const response = this.doGetResponse(group.lote, []);
      group.response = response;
      group.layer.setStyle(this.getStyle(group.lote, response));
      // `setContent` refresca solo si el popup está abierto; no hace falta
      // re-bindear (eso reintroduciría el click interno de Leaflet).
      group.popup?.setContent(this._popupContent(group.lote, response));
      this._setEtiqueta(group, response);
    });
  }

  /** Etiqueta permanente sobre el polígono: nombre del lote y, debajo, el
   *  número que explica su color.
   *
   *  Se liga acá a mano y no por `toolTipItems`/`TooltipRender` porque ese liga
   *  el tooltip una sola vez al montar el panel y solo lo reescribe cuando
   *  cambia su `<select>`: no se enteraría de los cambios de semana ni de
   *  plaga. Acá se liga una vez por capa y después solo se reemplaza el
   *  contenido, en el mismo `_refresh()` que repinta el estilo.
   *
   *  Sin esto, este era el único mapa del módulo sin etiquetas: había que abrir
   *  el popup lote por lote para saber cuál era cuál. */
  private _setEtiqueta(
    group: MapGroup<LoteSemanaResponse>,
    response: LoteSemanaResponse | null,
  ): void {
    const contenido = this._etiquetaContent(group.lote, response);

    if (group.layer.getTooltip()) {
      group.layer.setTooltipContent(contenido);
      return;
    }

    group.layer.bindTooltip(contenido, {
      className: "leaflet-lote-tooltip",
      direction: "center",
      permanent: true,
    });
  }

  /** Contenido de la etiqueta. El nombre siempre; el valor solo si el lote se
   *  recorrió y hay algo que reportar. */
  private _etiquetaContent(
    lote: ClienteFincaLoteResponse,
    response: LoteSemanaResponse | null,
  ): string {
    const detalle = this._etiquetaDetalle(response);
    if (!detalle) return lote.nombreLote;

    // El peso normal separa el dato del nombre sin pedir una clase nueva: la
    // `leaflet-lote-tooltip` de `map.css` ya aporta color y sombra.
    return `${lote.nombreLote}<br><span style="font-weight:400;opacity:0.92">${detalle}</span>`;
  }

  /** Segunda línea de la etiqueta. Con más de una plaga en juego —un grupo o el
   *  modo "Todas"— nombra la plaga (abreviada) además del valor: el color por sí
   *  solo dice que hay un problema, no cuál, y sin eso el modo sería un
   *  semáforo que no se puede accionar. */
  private _etiquetaDetalle(response: LoteSemanaResponse | null): string {
    if (!response) return "";

    const unica = this._getPlagaUnica();
    if (unica) {
      const evaluacion = evaluarEnLote(response.lote, unica.id, unica);
      return evaluacion ? formatNivel(evaluacion) : "";
    }

    const peor = getPeorPlagaEvaluada(
      response.lote,
      this._plagasById,
      this._seleccion,
    );
    if (!peor) return "";
    return `${getAbreviatura(peor.plaga)} ${formatNivel(peor.evaluacion)}`;
  }

  /** Filas del popup de un lote. */
  private _popupItems(
    lote: ClienteFincaLoteResponse,
    response: LoteSemanaResponse | null,
  ): PopupItem[] {
    const items: PopupItem[] = this.getDefaultPopupItems(lote);
    const plaga = this._getPlagaUnica();

    // Grupo o modo "Todas": el popup dice qué plaga está definiendo el color,
    // que es justo lo que el polígono no puede expresar.
    if (!plaga) {
      if (!response) return items;

      const peor = getPeorPlagaEvaluada(
        response.lote,
        this._plagasById,
        this._seleccion,
      );
      if (!peor) {
        items.push({ label: "Estado", content: _badge("ok", "Sin detección") });
        return items;
      }

      items.push({
        label: peor.plaga.nombreComun,
        content: _badge(
          peor.estado,
          `${formatNivel(peor.evaluacion)} · ${formatPorcentaje(peor.evaluacion.incidencia)} de árboles · ${ESTADO_LABEL[peor.estado]}`,
        ),
      });
      return items;
    }

    if (!response) {
      items.push({ label: plaga.nombreComun, content: "No monitoreado" });
      return items;
    }

    const evaluacion = evaluarEnLote(response.lote, plaga.id, plaga);
    if (!evaluacion) {
      items.push({ label: plaga.nombreComun, content: "No monitoreado" });
      return items;
    }

    // Sin estado no hay badge de color: es un benéfico o un organismo sin
    // valores de referencia, y pintarlo verde diría "está bien" donde
    // corresponde "no se puede evaluar".
    const detalle = `${formatNivel(evaluacion)} · ${formatPorcentaje(evaluacion.incidencia)} de árboles`;
    items.push({
      label: plaga.nombreComun,
      content:
        evaluacion.estado === null
          ? `${detalle} · sin valores de referencia`
          : _badge(
              evaluacion.estado,
              `${detalle} · ${ESTADO_LABEL[evaluacion.estado]}`,
            ),
    });
    return items;
  }

  /** HTML del popup, con el mismo formato que el resto de los mapas. */
  private _popupContent(
    lote: ClienteFincaLoteResponse,
    response: LoteSemanaResponse | null,
  ): string {
    const rows = this._popupItems(lote, response)
      .map(
        (item) =>
          `<tr><td class="map-popup-label">${item.label}</td><td width="1" class="map-popup-content">${item.content}</td></tr>`,
      )
      .join("");
    return `<div class="map-popup"><div class="map-popup-header"><div class="sx-card-title">${lote.nombreLote}</div></div><div class="map-popup-body"><table><tbody>${rows}</tbody></table></div></div>`;
  }
}

/** Badge del semáforo con el color del estado. Usa la clase global `sx-badge`
 *  con el color en línea: los tres colores son datos del semáforo, no una
 *  variante de botón. */
function _badge(estado: MonitoreoEstado, texto: string): string {
  return `<span class="sx-badge" style="background:${ESTADO_COLOR[estado]};color:#fff">${texto}</span>`;
}
